import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'react-toastify';

const Footer = () => {
  const { pathname } = useLocation();
  const currentYear = new Date().getFullYear();
  const { t, language } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address', { autoClose: 1000 });
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': language || 'en',
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Successfully subscribed to newsletter!', { autoClose: 1000 });
        setEmail('');
      } else {
        toast.error(data.detail || 'Failed to subscribe. Please try again.', { autoClose: 1000 });
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast.error('Failed to subscribe. Please try again later.', { autoClose: 1000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className={`bg-racing-black text-white mt-auto${pathname === '/' ? ' home-footer' : ''}`}>
      <div className="container-custom py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* About */}
          <div>
            <img 
              src="/logo-dark.png" 
              alt="Meller Racing" 
              className="h-10 w-auto mb-4"
            />
            <p className="text-gray-400 text-sm">
              {t('footer.aboutDesc')}
            </p>
          </div>

          {/* Customer Service */}
          <div>
            <h4 className="font-semibold mb-4">{t('footer.customerService')}</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/contact" className="hover:text-racing-red transition">{t('footer.contact')}</Link></li>
              <li><Link to="/faq" className="hover:text-racing-red transition">{t('footer.faq')}</Link></li>
              <li><Link to="/shipping" className="hover:text-racing-red transition">{t('footer.shipping')}</Link></li>
              <li><Link to="/returns" className="hover:text-racing-red transition">{t('footer.returns')}</Link></li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-semibold mb-4">{t('footer.newsletter')}</h4>
            <p className="text-sm text-gray-400 mb-4">
              {t('footer.newsletterDesc')}
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                placeholder={t('footer.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                className="flex-1 px-4 py-2 rounded-lg sm:rounded-r-none bg-gray-800 border border-gray-700 focus:outline-none focus:border-racing-red text-sm disabled:opacity-50"
              />
              <button 
                type="submit"
                disabled={loading}
                className="bg-racing-red px-4 py-2 rounded-lg sm:rounded-l-none hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold whitespace-nowrap"
              >
                {loading ? '...' : t('footer.subscribe')}
              </button>
            </form>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
          <p>© {currentYear} Meller Racing. {t('footer.allRights')}</p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <a href={process.env.REACT_APP_FACEBOOK_URL || 'https://www.facebook.com/p/Meller-Racing-warsztat-i-hamownia-motocyklowa-100083175588341/'} target="_blank" rel="noopener noreferrer" className="hover:text-racing-red transition">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a href={process.env.REACT_APP_INSTAGRAM_URL || 'https://www.instagram.com/meller_racing_/'} target="_blank" rel="noopener noreferrer" className="hover:text-racing-red transition">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
